import { Routes, Route, useNavigate, useParams } from "react-router-dom";

import { motion } from "framer-motion";
import { useState } from "react";

import Navbar from '../components/navabar';
import Footer from '../components/footer';

// Main How It Works Component
const HowItWorks = () => {
  const [activeSection, setActiveSection] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren",
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const sections = [
    {
      title: "Attending an Event",
      description: "All you need to know about attending an event",
      component: <AttendingEvent goBack={() => setActiveSection(null)} />,
    },
    {
      title: "Requesting a Refund",
      description: "All you need to know about requesting a refund",
      component: <RequestingRefund goBack={() => setActiveSection(null)} />,
    },
    {
      title: "Contacting an Event Organizer",
      description: "All you need to know about contacting an Event Organizer",
      component: <ContactingOrganizer goBack={() => setActiveSection(null)} />,
    },
  ];

  const organizerSections = [
    {
      title: "Creating Ticket Type",
      description: "All you need to know about creating ticket type",
      component: <CreatingTicketType goBack={() => setActiveSection(null)} />,
    },
    {
      title: "Creating an Event",
      description: "All you need to know about creating an event",
      component: <CreatingEvent goBack={() => setActiveSection(null)} />,
    },
    {
      title: "Adding Images & Video to Your Event Listing",
      description: "All you need to know about adding media to your event",
      component: <AddingMedia goBack={() => setActiveSection(null)} />,
    },
  ];

  if (activeSection) {
    return activeSection;
  }

  return (
    <>
    <Navbar />
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-4xl mx-auto">
        <motion.h1
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold text-gray-900 mb-8"
        >
          How It Works
        </motion.h1>

        <div className="mb-2">
          <h2 className="text-lg font-medium text-gray-700 mb-4">
            Search Help Articles
          </h2>
          <div className="relative">
            <input
              type="text"
              placeholder="Search help articles..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <div className="absolute right-3 top-2.5 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <h2 className="text-lg font-medium text-gray-700 mt-8 mb-4">
          Event Attendee
        </h2>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-12"
        >
          {sections.map((section, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="bg-white p-6 rounded-lg shadow-md border border-gray-100 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection(section.component)}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {section.title}
              </h3>
              <p className="text-gray-600">{section.description}</p>
              <div className="mt-4 text-[#FCD20B] flex items-center">
                <span>Learn more</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <h2 className="text-xl font-bold text-gray-900 mt-12 mb-6">
          Event Organizer
        </h2>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {organizerSections.map((section, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="bg-white p-6 rounded-lg shadow-md border border-gray-100 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection(section.component)}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {section.title}
              </h3>
              <p className="text-gray-600">{section.description}</p>
              <div className="mt-4 text-[#FCD20B] flex items-center">
                <span>Learn more</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-12 pt-6 border-t border-gray-200">
          <button
            onClick={() => setActiveSection(<HowItWorks goBack={() => setActiveSection(null)} />)}
            className="px-6 py-2 bg-[#FCD20B] text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            Contact Us
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 ml-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          </button>
        </div>
      </div>
    </motion.div>
    <Footer />
    </>
  );
};


// Attending an Event Component
const AttendingEvent = () => {
  const navigate = useNavigate();

  return (
     <>
    <Navbar />
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("")}
          className="mb-6 flex items-center text-[#FCD20B] hover:text-blue-800 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to How It Works
        </button>

        <motion.div
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white p-6 sm:p-8 rounded-lg shadow-md"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Attending an Event on Eventli
          </h1>
          
          <p className="text-gray-700 mb-6">
            Welcome! Whether you're new or returning, here's how to access your tickets, 
            get help logging in, and troubleshoot missing orders.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            How to Find Your Tickets
          </h2>
          
          <p className="text-gray-700 mb-4">
            After booking a ticket, we send a confirmation email to the address you used at checkout. 
            You can access your ticket in the way below:
          </p>

          <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">
            Using the Website
          </h3>
          
          <p className="text-gray-700 mb-6">
            Go to Eventli.com, log into your account, and click My Tickets at the top right.
          </p>

          <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">
            Check Your Email
          </h3>
          
          <p className="text-gray-700 mb-6">
            A confirmation email with a download link will be sent after checkout. Don't see it? 
            Be sure to check your Spam or Promotions folder.
          </p>

          <div className="flex items-start mb-6">
            <input type="checkbox" id="trouble-login" className="mt-1 mr-3" />
            <label htmlFor="trouble-login" className="text-gray-700">
              <span className="font-medium">Trouble Logging In?</span>
              <div className="ml-6 mt-2 pl-4 border-l-2 border-gray-200">
                <p className="mb-2">If you can't access your account:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Go to the login page and enter the same email you used when ordering.</li>
                  <li>If you haven't set a password yet, we'll email you a link to do so.</li>
                  <li>Once your password is set, you'll be able to log in and find your tickets under My Tickets.</li>
                </ul>
              </div>
            </label>
          </div>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Tickets Not Showing? Here's Why
          </h2>
          
          <p className="text-gray-700 mb-4">
            If your ticket isn't appearing, it might be because of:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>
              <span className="font-medium">Wrong email login:</span> Make sure you're signed in with the same email used during purchase.
            </li>
            <li>
              <span className="font-medium">Misspelled email at checkout:</span> A typo could prevent your order from being linked.
            </li>
            <li>
              <span className="font-medium">Digital ticket disabled:</span> The event organizer may have disabled PDF downloads — but your order will still show.
            </li>
          </ul>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2 bg-[#FCD20B] text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
    <Footer />
    </>
  );
};

// Requesting a Refund Component
const RequestingRefund = () => {
  const navigate = useNavigate();

  return (
     <>
    <Navbar />
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("")}
          className="mb-6 flex items-center text-[#FCD20B] hover:text-blue-800 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to How It Works
        </button>

        <motion.div
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white p-6 sm:p-8 rounded-lg shadow-md"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Requesting a Refund on Eventli
          </h1>
          
          <p className="text-gray-700 mb-6">
            Ticket refunds are handled directly by the event organizers. If something changes and you can't attend, 
            you may be able to request a refund — depending on the organizer's refund policy.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            How to Request a Refund from Your Eventli Account
          </h2>
          
          <p className="text-gray-700 mb-4">
            If the event organizer has enabled refund requests for their event:
          </p>

          <ol className="list-decimal pl-5 space-y-2 mb-6">
            <li>Log in to your Eventli account (use the same email you used when buying your ticket).</li>
            <li>Click My Tickets in the menu.</li>
            <li>Find the event you want a refund for and click Request Refund.</li>
            <li>Fill in your reason and submit your request.</li>
          </ol>

          <p className="text-gray-700 mb-6">
            The organizer will review your request and respond, typically within 5 business days.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Don't See the Refund Option?
          </h2>
          
          <p className="text-gray-700 mb-6">
            If the "Request Refund" button isn't available:
          </p>

          <ol className="list-decimal pl-5 space-y-2 mb-6">
            <li>It means the organizer hasn't enabled refunds for that event.</li>
            <li>Instead, click Contact Organizer on the event page to send them a direct message.</li>
          </ol>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Refunds for Cancelled or Postponed Events:
          </h2>
          
          <p className="text-gray-700 mb-4">
            Some exceptions apply — you may still qualify for a refund if:
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">Situation</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">What You Can Do</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">✗ Event was cancelled</td>
                  <td className="px-4 py-3 border-b border-gray-200">You may request a refund within 45 days.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">☉ Event was postponed for 90+ days (no new date)</td>
                  <td className="px-4 py-3 border-b border-gray-200">You may request a refund before a new date is set.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-gray-700 mb-6">
            If your event fits either case, reach out to Eventli Support using the Refund Support Form. 
            Note: Refunds are only available for payments processed through Eventli's payment system.
          </p>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <span className="font-medium">Please Note:</span> Refund policies vary by organizer. Some tickets may be marked non-refundable at purchase. Refunds will go back to the original payment method if approved.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2 bg-[#FCD20B] text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
    <Footer />
    </>
  );
};

// Contacting an Event Organizer Component
const ContactingOrganizer = () => {
  const navigate = useNavigate();

  return (
     <>
    <Navbar />
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("")}
          className="mb-6 flex items-center text-[#FCD20B] hover:text-blue-800 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to How It Works
        </button>

        <motion.div
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white p-6 sm:p-8 rounded-lg shadow-md"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Contacting an Event Organizer on Eventli
          </h1>
          
          <p className="text-gray-700 mb-6">
            Have a question about an event? Eventli allows organizers to manage their own events directly. 
            If you need help or more details, it's best to reach out to the organizer directly through 
            their event page or your account.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            If You Already Have a Ticket
          </h2>
          
          <p className="text-gray-700 mb-4">
            Once you've registered for an event, you can access support options right from your Eventli account.
          </p>

          <p className="text-gray-700 mb-4">
            Here's what you can do:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Update your ticket details (if the organizer allows)</li>
            <li>Request a refund (if eligible)</li>
            <li>Join an online event directly from the event page (if it's virtual)</li>
          </ul>

          <p className="text-gray-700 mb-4 font-medium">
            Need more help? Contact the organizer directly:
          </p>

          <ol className="list-decimal pl-5 space-y-2 mb-6">
            <li>Log in to your Eventli account.</li>
            <li>Click on My Tickets.</li>
            <li>Find the event and select Contact Organizer.</li>
            <li>Fill out the form with your question or request.</li>
            <li>Review and click Submit.</li>
          </ol>

          <p className="text-gray-700 mb-6">
            The organizer will get your message and respond to you directly.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            If You Don't Have a Ticket Yet
          </h2>
          
          <p className="text-gray-700 mb-6">
            Have a question before registering? No problem — you can still reach out:
          </p>

          <p className="text-gray-700 mb-4">
            Here's what you can do:
          </p>

          <ol className="list-decimal pl-5 space-y-2 mb-6">
            <li>Go to the event's page on Eventli.</li>
            <li>Scroll down and select Contact Organizer.</li>
            <li>Fill in the contact form with your message.</li>
            <li>Click Continue, review, and then select Submit Request.</li>
          </ol>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Please Note:</span> Eventli is a platform for connecting attendees and organizers. Since events are managed by third-party organizers, Eventli can't make changes or provide answers on their behalf.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2 bg-[#FCD20B] text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
    <Footer />
    </>
  );
};

// Creating Ticket Type Component
const CreatingTicketType = () => {
  const navigate = useNavigate();

  return (
     <>
    <Navbar />
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("")}
          className="mb-6 flex items-center text-[#FCD20B] hover:text-blue-800 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to How It Works
        </button>

        <motion.div
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white p-6 sm:p-8 rounded-lg shadow-md"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Creating Ticket Types on Eventli
          </h1>
          
          <p className="text-gray-700 mb-6">
            Create and customize your ticket types based on your event needs. Set pricing, 
            quantity, delivery methods, and more — all from your event dashboard.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Step 1: Open Your Event
          </h2>
          
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Log in to your Eventli account.</li>
            <li>Go to My Events → Select your event.</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Step 2: Add or Manage Tickets
          </h2>
          
          <p className="text-gray-700 mb-6">
            Choose to Create New or Copy from a Previous Event.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Step 3: Set Up Ticket Type
          </h2>
          
          <p className="text-gray-700 mb-4">
            Choose a ticket type:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li><span className="font-medium">Paid</span> - Set a fixed price.</li>
            <li><span className="font-medium">Free</span> - No charge to attend.</li>
            <li><span className="font-medium">Donation</span> - Attendee sets the price.</li>
          </ul>

          <p className="text-gray-700 mb-4">
            Then enter:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Ticket Name (e.g., Early Bird, VIP)</li>
            <li>Quantity Available</li>
            <li>Price (for paid tickets)</li>
            <li>Sales Dates - When ticket sales begin and end</li>
            <li>(For recurring events: set timing relative to event start time.)</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Step 4: Advanced Ticket Settings (Optional)
          </h2>
          
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">Setting</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Description</td>
                  <td className="px-4 py-3 border-b border-gray-200">Add more context for attendees.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Sales Channel</td>
                  <td className="px-4 py-3 border-b border-gray-200">Choose where the ticket can be sold. Online, At-the-door, or Everywhere</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Visibility</td>
                  <td className="px-4 py-3 border-b border-gray-200">Show or hide the ticket. Hidden tickets can only be accessed via promo code.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Purchase Limit</td>
                  <td className="px-4 py-3 border-b border-gray-200">Set min/max number of tickets per order. Useful for bundles or group sales.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Delivery Method</td>
                  <td className="px-4 py-3 border-b border-gray-200">Choose Email (sent via email) or Will Call (picks up at event).</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Sales End Visibility</td>
                  <td className="px-4 py-3 border-b border-gray-200">Choose whether to display the sale end date at checkout.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Step 5: Save Your Ticket
          </h2>
          
          <p className="text-gray-700 mb-6">
            Once all fields are filled, click Save to create your ticket.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Customize Checkout Settings
          </h2>
          
          <p className="text-gray-700 mb-4">
            After adding tickets, go to Settings to:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Display number of tickets remaining</li>
            <li>Details from ticketed to registration event (for free events)</li>
            <li>Add a message to show after sales end</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Updating an Existing Ticket
          </h2>
          
          <p className="text-gray-700 mb-4">
            To edit or manage an existing ticket:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Click the 3-dot menu beside any ticket:</li>
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li><span className="font-medium">Edit</span> - Change name, price, quantity, and settings.</li>
              <li><span className="font-medium">Copy</span> - Duplicate the ticket with same setup.</li>
              <li><span className="font-medium">Delete</span> - Remove ticket (only if it hasn't had any sales).</li>
            </ul>
          </ul>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Tickets that have been sold (even refunded) can't be deleted.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Troubleshooting Ticket Visibility
          </h2>
          
          <p className="text-gray-700 mb-4">
            If your ticket isn't showing or can't be purchased, check:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Make sure the ticket is within its active sales window.</li>
            <li>Availability - Ensure tickets aren't sold out or the event capacity isn't reached.</li>
            <li>Visibility - Hidden tickets require a promo code to appear.</li>
            <li>Sales Channel - Ensure it's set to "Everywhere" or "Online" if you want tickets visible online.</li>
          </ul>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2 bg-[#FCD20B] text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
    <Footer />
    </>
  );
};


const CreatingEvent = () => {
  const navigate = useNavigate();

  return (
     <>
    <Navbar />
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("")}
          className="mb-6 flex items-center text-[#FCD20B] hover:text-blue-800 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to How It Works
        </button>

        <motion.div
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white p-6 sm:p-8 rounded-lg shadow-md"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Creating an Event on Eventli
          </h1>
          
          <p className="text-gray-700 mb-6">
            Creating an event on Eventli is easy and flexible. Whether you're hosting a concert, 
            workshop, festival, or private gathering, follow these steps to set up your event 
            and start selling tickets.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Step 1: Go to "My Events"
          </h2>
          
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Log in to your Eventli account</li>
            <li>From your Dashboard, click Create Event</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Step 2: Upload Media
          </h2>
          
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Add your event banner image (2160 x 1080px recommended, JPEG or PNG)</li>
            <li>Upload a short promo video (optional, under 1 min, MP4/MOV)</li>
            <li>Include extra images in the event description for a more engaging listing</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Step 3: Fill in Your Event Details
          </h2>
          
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">Field</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">What to Enter</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Event Title</td>
                  <td className="px-4 py-3 border-b border-gray-200">Give your event a clear and catchy name</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Category & Tags</td>
                  <td className="px-4 py-3 border-b border-gray-200">Choose a category and relevant tags</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Location</td>
                  <td className="px-4 py-3 border-b border-gray-200">Enter venue details or select "Online Event"</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Date & Time</td>
                  <td className="px-4 py-3 border-b border-gray-200">Choose start and end time; set recurring if needed</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Event Description</td>
                  <td className="px-4 py-3 border-b border-gray-200">Tell people what to expect (can use rich text + images)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Step 4: Add Tickets
          </h2>
          
          <p className="text-gray-700 mb-4">
            Click 'Add Tickets' to set up how attendees can register:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Choose Ticket Type: Paid, Free, or Donation</li>
            <li>Set ticket name, price, quantity, and sale dates</li>
            <li>Enable advanced settings: delivery method, group limits, visibility, promo codes</li>
          </ul>

          <p className="text-gray-700 mb-6">
            You can create multiple ticket tiers (e.g., Early Bird, VIP, General Admission).
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Step 5: Save & Preview Your Event
          </h2>
          
          <p className="text-gray-700 mb-6">
            After uploading your media:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Click Save Draft if you're not ready to publish</li>
            <li>Or click Preview to see how the event looks to attendees</li>
            <li>When ready, hit Publish Event to go live!</li>
          </ul>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2 bg-[#FCD20B] text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
    <Footer />
    </>
  );
};


const AddingMedia = () => {
  const navigate = useNavigate();

  return (
     <>
    <Navbar />
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("")}
          className="mb-6 flex items-center text-[#FCD20B] hover:text-blue-800 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to How It Works
        </button>

        <motion.div
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white p-6 sm:p-8 rounded-lg shadow-md"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Add Images & Video to Your Event Listing on Eventli
          </h1>
          
          <p className="text-gray-700 mb-6">
            Make your event stand out with high-quality visuals. Add a banner image, carousel photos, 
            or even a short video to bring your event to life. These visuals appear on your event page, 
            in search results, and in promotional campaigns across Eventli.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Where to Add Media
          </h2>
          
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Log in to your Eventli account</li>
            <li>Go to My Events → Select your event</li>
            <li>Click on Build Event Page</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Upload Your Main Event Image
          </h2>
          
          <p className="text-gray-700 mb-6">
            Your main banner is the first thing attendees will see, so make it pop.
          </p>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            + To upload:
          </h3>
          
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Click Upload image</li>
            <li>Choose a file from your device</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">
            Image Guidelines:
          </h3>
          
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">Requirement</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">Details</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">File Type</td>
                  <td className="px-4 py-3 border-b border-gray-200">JPEG or PNG only</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">File Size</td>
                  <td className="px-4 py-3 border-b border-gray-200">Max 10MB</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Recommended Size</td>
                  <td className="px-4 py-3 border-b border-gray-200">2160 x 1080 px (2:1 ratio)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Focus Point Selection</td>
                  <td className="px-4 py-3 border-b border-gray-200">Select the most important part of the image (51 preview)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200">Ownership</td>
                  <td className="px-4 py-3 border-b border-gray-200">Only upload images you own or have rights to use</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-gray-700 mb-6">
            Use wide, clean, and colorful Images — text overlays should be minimal for better visibility.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Optional: Add a Short Video
          </h2>
          
          <p className="text-gray-700 mb-6">
            Want to show a quick teaser or promo clip? Upload a short video that plays in your media carousel.
          </p>

          <h3 className="text-lg font-medium text-gray-800 mb-3">
            Video Requirements:
          </h3>
          
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full bg-white border border-gray-200">
              <tbody>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200 font-medium">File Format</td>
                  <td className="px-4 py-3 border-b border-gray-200">MP4 or MOV</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200 font-medium">Duration</td>
                  <td className="px-4 py-3 border-b border-gray-200">1 minute max</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200 font-medium">Resolution</td>
                  <td className="px-4 py-3 border-b border-gray-200">At least 480p</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200 font-medium">Orientation</td>
                  <td className="px-4 py-3 border-b border-gray-200">Vertical or horizontal (9:16 preferred)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 border-b border-gray-200 font-medium">File Size</td>
                  <td className="px-4 py-3 border-b border-gray-200">Up to 1GB</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Add Images to Your Event Description
          </h2>
          
          <p className="text-gray-700 mb-6">
            To include supporting visuals inside your event description:
          </p>

          <ol className="list-decimal pl-5 space-y-2 mb-6">
            <li>Scroll to About This Event</li>
            <li>Click the + button to open the editor</li>
            <li>Type your description</li>
            <li>Click Add Image wherever needed</li>
            <li>Select the image you want to insert</li>
          </ol>

          <p className="text-gray-700 mb-6">
            Note: Images inside the description won't appear in search or promo cards — only on your event page.
          </p>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Additional Tips
          </h2>
          
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>You can upload multiple images — they'll display as a carousel.</li>
            <li>If you don't upload a video, your main image will appear as the preview in search.</li>
            <li>Canva Users: Design your banner using the recommended size (2160x1080px), export as PNG or JPG, then upload it to Eventli.</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
            Don't Forget to Save!
          </h2>
          
          <p className="text-gray-700 mb-6">
            After uploading your media:
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Click Save Changes</li>
            <li>Preview your event page to ensure everything looks perfect on desktop and mobile.</li>
          </ul>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2 bg-[#FCD20B] text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>

    <Footer />
    </>
  );
};

const HelpCenter = () => {
  return <HowItWorks />;
};

export default HelpCenter;